import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NewInterface } from '@dr-customer-offers-ui/lib-interfaces';
import { ApiEndpointsService } from '@dr-customer-offers-ui/lib-services';
import { Store } from '@ngrx/store';
import { NgxIntervalDataGridService } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import { NgxDateRangePickerOutput } from 'ngx-date-range-picker';
import { NgxDropdownOption } from 'ngx-dropdown';
import { CookieService } from 'ngx-shared-services';
import { filter, Observable, Subject, take } from 'rxjs';
import { environment } from '../environments/environment';
import { IconSize } from './shared/icons/icons-size.enum';
import { TabOption, TabOptionsEnum } from './shared/models';
import { Role, RoleTypeEnum } from './shared/models/role';
import { UserPreference } from './shared/models/user-preference';
import { DataViewModelService } from './shared/services/data-vm.service';
import { InternalService } from './shared/services/internal.service';
import { LoaderService } from './shared/services/loader.service';
import { selectRoles } from './store/selectors/com.selectors';

@Component({
  selector: 'dr-customer-offers-ui-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  protected TabOptionsEnum = TabOptionsEnum;
  protected appTitleValue!: NewInterface;
  protected navUrl!: string;
  protected selectedWeekRange!: NgxDateRangePickerOutput;
  protected exportIconSize: IconSize = IconSize.Large;
  protected selectedRegFormControl$: Observable<NgxDropdownOption> = this.dataVMService.selectedRegFormControl.valueChanges;
  errorMessageTitle = 'Oh snap!';
  message = 'You do not have Authorization to view offers for this Registration.';

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(ApiEndpointsService)
    private api: ApiEndpointsService,
    public dialog: MatDialog,
    private internalService: InternalService,
    private translate: TranslateService,
    private cookieService: CookieService,
    private dataVMService: DataViewModelService,
    public loader: LoaderService,
    private ngxService: NgxIntervalDataGridService,
    private router: Router,
    private store: Store
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.navUrl = this.api.globalNavUrl;

    this.store
      .select(selectRoles)
      .pipe(
        filter((ur): ur is Role[] => ur.length > 0),
        take(1)
      )
      .subscribe((ur: Role[]) => {
        const hasAdminRole = !!ur.find((resource: Role) => resource.businessRole === RoleTypeEnum.DR_ADMIN);
        const hasComUserRole = !!ur.find((resource: Role) => resource.businessRole === RoleTypeEnum.CUSTOMER_OFFER_USER);

        this.dataVMService.externalUserSignal.set(hasComUserRole && !hasAdminRole);
      });

    const { defaults } = environment;
    const locale = this.cookieService.getI18NLocale() || defaults.locale;
    const apiLocale = this.cookieService.getRawI18nLocale() || defaults.apiLocale;
    const brand = this.cookieService.getI18NBrand() || defaults.brand.toUpperCase();
    this.translate.setDefaultLang(`${defaults.locale}.${defaults.brand.toUpperCase()}`);
    this.translate.use(`${locale}.${brand}`);

    const userPreference: UserPreference = {
      locale,
      apiLocale: apiLocale,
      brand: brand
    };
    this.internalService.userPreferenceSignal.set(userPreference);
    this.router.navigateByUrl('/');
  }

  protected tabChanged(selectedTabOption: TabOption): void {
    this.dataVMService.selectedTabOptionSignal.set(selectedTabOption);
    this.ngxService.clearUnSavedData();
  }
}
